/**
 * @fileoverview Single Sudoku Cell Component
 * @description Renders a single cell in the Sudoku grid with proper styling
 */

import React, { memo } from "react";
import { View, Text, Pressable, useColorScheme } from "react-native";

interface SudokuCellProps {
  /** Cell value (1-9 or null/0 for empty) */
  value: number | null;
  /** Whether this is a pre-filled (given) cell */
  isGiven: boolean;
  /** Whether this cell is currently selected */
  isSelected: boolean;
  /** Whether this cell is in same row/col/box as selected */
  isHighlighted: boolean;
  /** Whether this cell has the same number as selected */
  isSameNumber: boolean;
  /** Whether this cell value is incorrect */
  isError: boolean;
  /** Whether this cell was revealed by a hint */
  isHint: boolean;
  /** Notes (pencil marks) for this cell */
  notes: number[];
  /** Cell index (0-80) */
  index: number;
  /** Cell size in pixels */
  cellSize: number;
  /** Called when cell is pressed */
  onPress: () => void;
  /** Whether interactions are disabled */
  disabled?: boolean;
}

/**
 * A single cell in the Sudoku grid
 */
export const SudokuCell = memo<SudokuCellProps>(
  ({
    value,
    isGiven,
    isSelected,
    isHighlighted,
    isSameNumber,
    isError,
    isHint,
    notes,
    index,
    cellSize,
    onPress,
    disabled = false,
  }) => {
    const colorScheme = useColorScheme();
    const isDark = colorScheme === "dark";

    // Calculate position for accessibility
    const row = Math.floor(index / 9);
    const col = index % 9;

    // Calculate font size based on cell size
    const mainFontSize = cellSize * 0.55;
    const noteFontSize = cellSize * 0.22;

    // Background color based on state
    const getBackgroundColor = () => {
      if (isSelected) {
        return isDark ? "#3B82F6" : "#3B82F6"; // Blue for selected
      }
      if (isSameNumber && value !== null) {
        return isDark ? "#1E3A5F" : "#BFDBFE"; // Light blue for same number
      }
      if (isHighlighted) {
        return isDark ? "#1F2937" : "#F3F4F6"; // Gray for highlighted
      }
      return isDark ? "#111827" : "#FFFFFF";
    };

    // Text color based on state
    const getTextColor = () => {
      if (isSelected) {
        return "#FFFFFF";
      }
      if (isError) {
        return "#EF4444"; // Red for errors
      }
      if (isHint) {
        return "#10B981"; // Green for hints
      }
      if (isGiven) {
        return isDark ? "#F9FAFB" : "#111827"; // Strong color for given
      }
      return isDark ? "#60A5FA" : "#3B82F6"; // Blue for user entries
    };

    return (
      <Pressable
        onPress={onPress}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={`Cel rij ${row + 1} kolom ${col + 1}${value ? `, waarde ${value}` : ", leeg"}`}
        accessibilityState={{ selected: isSelected, disabled }}
        style={{
          flex: 1,
          backgroundColor: getBackgroundColor(),
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {value !== null && value !== 0 ? (
          <Text
            style={{
              fontSize: mainFontSize,
              fontWeight: isGiven ? "700" : "500",
              color: getTextColor(),
            }}
          >
            {value}
          </Text>
        ) : notes.length > 0 ? (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              width: cellSize - 4,
              height: cellSize - 4,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <View
                key={n}
                style={{
                  width: (cellSize - 4) / 3,
                  height: (cellSize - 4) / 3,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {notes.includes(n) && (
                  <Text
                    style={{
                      fontSize: noteFontSize,
                      color: isDark ? "#9CA3AF" : "#6B7280",
                    }}
                  >
                    {n}
                  </Text>
                )}
              </View>
            ))}
          </View>
        ) : null}
      </Pressable>
    );
  }
);

SudokuCell.displayName = "SudokuCell";

